import React, { Fragment, useEffect, useState, useCallback } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import MetaTags from "react-meta-tags";
import LayoutOne from "../../layouts/LayoutOne";
import { AppInstance } from "../../api/instance";
import { ratingReviewApi } from "../../api/ratingReviewApi";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useSettings } from "../../hooks/useSettings";

const BODY_MAX = 1000;

const ProductReview = ({ userInfo }) => {
  const { id: orderItemId } = useParams();
  const history = useHistory();
  const { siteName } = useSettings();

  const [orderDetail, setOrderDetail] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  const [myReview, setMyReview] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);

  const [rating, setRating] = useState(0);
  const [reviewBody, setReviewBody] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mapReviewErrorMessage = (message) => {
    if (message === "You can only review completed order items.") {
      return "Review is available only after delivery is completed.";
    }
    if (message === "You do not own this order item.") {
      return "You cannot review this item.";
    }
    return message || "Something went wrong.";
  };

  const getPrimaryImageUrl = (item) => {
    if (!item) return "";
    if (item.images && item.images.length > 0) {
      const sorted = [...item.images].sort(
        (a, b) => (a.position || 0) - (b.position || 0)
      );
      return sorted[0]?.url || "";
    }
    if (item.product && item.product.cover) {
      return process.env.REACT_APP_IMAGE_BASE_URL + item.product.cover;
    }
    return "";
  };

  const getDeliveryLine = (statuses) => {
    if (!Array.isArray(statuses) || statuses.length === 0) return null;
    const delivered = statuses.find(
      (s) => s.status === "COMPLETED" || s.status === "DELIVERED"
    );
    if (!delivered?.created_at) return null;
    const d = new Date(delivered.created_at);
    return `Delivered on ${d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    })}`;
  };

  const fetchOrderDetail = useCallback(async () => {
    setPageLoading(true);
    setPageError(null);
    try {
      const { data } = await AppInstance.get(`/order-item`, {
        params: { id: orderItemId },
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      setOrderDetail(data.data);
    } catch (e) {
      console.log(e);
      setPageError("Failed to load order details.");
      setOrderDetail(null);
    } finally {
      setPageLoading(false);
    }
  }, [orderItemId, userInfo]);

  const fetchMyReview = useCallback(async () => {
    if (!orderItemId) return;
    setReviewLoading(true);
    setFormError("");
    try {
      const { data } = await ratingReviewApi.getMyReview(orderItemId);
      const reviewData = data?.data || null;
      setMyReview(reviewData);
      if (reviewData) {
        const r = Number(reviewData.rating);
        setRating(Number.isFinite(r) ? Math.min(5, Math.max(0, Math.round(r))) : 0);
        setReviewBody(String(reviewData.review || "").slice(0, BODY_MAX));
      } else {
        setRating(0);
        setReviewBody("");
      }
    } catch (e) {
      setMyReview(null);
      setFormError(
        mapReviewErrorMessage(
          e?.response?.data?.message || "Failed to load your review."
        )
      );
    } finally {
      setReviewLoading(false);
    }
  }, [orderItemId]);

  useEffect(() => {
    if (userInfo?.token) {
      fetchOrderDetail();
    }
  }, [userInfo, fetchOrderDetail]);

  const latestStatus = orderDetail?.statuses?.[0]?.status;
  const canReview = latestStatus === "COMPLETED";

  useEffect(() => {
    if (orderDetail?.id && canReview) {
      fetchMyReview();
    }
  }, [orderDetail?.id, canReview, fetchMyReview]);

  const validate = () => {
    const errors = {};
    if (rating < 1 || rating > 5) {
      errors.rating = "Please select a star rating.";
    }
    if (reviewBody.length > BODY_MAX) {
      errors.reviewBody = `Review cannot exceed ${BODY_MAX} characters.`;
    }
    return errors;
  };

  const isFormValid =
    rating >= 1 &&
    rating <= 5 &&
    reviewBody.length <= BODY_MAX;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setFormError("");
    setIsSubmitting(true);
    try {
      await ratingReviewApi.upsertReview({
        order_item_id: orderDetail.id,
        rating,
        review: String(reviewBody || "").trim(),
      });
      history.push({
        pathname: `${process.env.PUBLIC_URL}/order-detail/${orderItemId}`,
        state: {
          showReviewSuccess: true,
        },
      });
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        setFieldErrors(apiErrors);
      }
      setFormError(mapReviewErrorMessage(err?.response?.data?.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const item = orderDetail?.item;
  const productName = item
    ? `${item.product?.name || ""}${item.name ? ` - ${item.name}` : ""}`.trim()
    : "";
  const brandRaw = item?.product?.brand;
  const brandName =
    typeof brandRaw === "string"
      ? brandRaw
      : brandRaw?.name || "";
  const deliveryLine = getDeliveryLine(orderDetail?.statuses);

  if (pageLoading) {
    return (
      <Fragment>
        <MetaTags>
          <title>{siteName} | Product review</title>
        </MetaTags>
        <LayoutOne headerTop="visible" headerContainerClass>
          <div className="product-review-page pt-90 pb-100">
            <div className="container">
              <div className="text-center py-5">
                <div className="flone-preloader">
                  <span></span>
                  <span></span>
                </div>
                <p className="mt-3 text-muted">Loading…</p>
              </div>
            </div>
          </div>
        </LayoutOne>
      </Fragment>
    );
  }

  if (pageError || !orderDetail) {
    return (
      <Fragment>
        <MetaTags>
          <title>{siteName} | Product review</title>
        </MetaTags>
        <LayoutOne headerTop="visible" headerContainerClass>
          <div className="product-review-page pt-90 pb-100">
            <div className="container">
              <p className="text-center">{pageError || "Order not found."}</p>
              <div className="text-center mt-3">
                <Link
                  to={process.env.PUBLIC_URL + "/my-orders"}
                  className="btn btn-primary"
                >
                  Back to orders
                </Link>
              </div>
            </div>
          </div>
        </LayoutOne>
      </Fragment>
    );
  }

  if (!canReview) {
    return (
      <Fragment>
        <MetaTags>
          <title>{siteName} | Product review</title>
        </MetaTags>
        <LayoutOne headerTop="visible" headerContainerClass>
          <div className="product-review-page pt-90 pb-100">
            <div className="container product-review-container">
              <Link
                to={process.env.PUBLIC_URL + "/order-detail/" + orderItemId}
                className="product-review-back d-inline-flex align-items-center mb-4"
              >
                <i className="pe-7s-angle-left me-1" />
                Back to tracking details
              </Link>
              <p className="text-muted">
                Reviews are available only after this order is completed.
              </p>
              <Link
                to={process.env.PUBLIC_URL + "/order-detail/" + orderItemId}
                className="btn btn-outline-primary mt-2"
              >
                View order
              </Link>
            </div>
          </div>
        </LayoutOne>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <MetaTags>
        <title>{siteName} | Product review</title>
        <meta
          name="description"
          content={`Write a product review — ${siteName}`}
        />
      </MetaTags>

      <LayoutOne headerTop="visible" headerContainerClass>
        <div className="product-review-page pt-90 pb-100">
          <div className="container product-review-container">
            <Link
              to={process.env.PUBLIC_URL + "/order-detail/" + orderItemId}
              className="product-review-back d-inline-flex align-items-center mb-3"
            >
              <i className="pe-7s-angle-left me-1" />
              Back to tracking details
            </Link>

            <h1 className="product-review-title">Product review</h1>
            <p className="product-review-subtitle mb-4">
              Help others know what to buy!
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="product-review-card">
                <div className="product-review-product-row">
                  <div className="product-review-product-thumb">
                    <img
                      src={getPrimaryImageUrl(item)}
                      alt=""
                      className="img-fluid rounded"
                    />
                  </div>
                  <div className="product-review-product-meta">
                    {brandName ? (
                      <div className="product-review-brand">{brandName}</div>
                    ) : null}
                    <div className="product-review-product-name">{productName}</div>
                    {deliveryLine ? (
                      <div className="product-review-delivered">{deliveryLine}</div>
                    ) : null}
                  </div>
                </div>

                {reviewLoading ? (
                  <div className="text-center py-4">
                    <div
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                    >
                      <span className="visually-hidden">Loading…</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="product-review-section">
                      <div className="product-review-label mb-2">
                        How do you rate this product?
                      </div>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <div
                          className="product-review-stars"
                          role="group"
                          aria-label="Star rating"
                        >
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              className="product-review-star-btn"
                              aria-label={`${star} star${star > 1 ? "s" : ""}`}
                              onClick={() => {
                                setRating(star);
                                setFieldErrors((prev) => ({
                                  ...prev,
                                  rating: undefined,
                                }));
                                setFormError("");
                              }}
                            >
                              <i
                                className={`fa fa-star ${
                                  star <= rating ? "is-active" : ""
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                        {rating > 0 ? (
                          <span className="product-review-saved ms-1">
                            <i className="fa fa-check me-1" aria-hidden="true" />
                            Saved
                          </span>
                        ) : null}
                      </div>
                      {fieldErrors.rating && (
                        <div className="text-danger small mt-1">
                          {Array.isArray(fieldErrors.rating)
                            ? fieldErrors.rating[0]
                            : fieldErrors.rating}
                        </div>
                      )}
                    </div>

                    <div className="product-review-section mt-4">
                      <div className="product-review-section-heading mb-3">
                        Write a product review
                      </div>

                      <div className="product-review-field">
                        <div className="product-review-input-wrap product-review-input-wrap--textarea">
                          <textarea
                            className={`form-control product-review-textarea ${
                              fieldErrors.reviewBody || fieldErrors.review
                                ? "is-invalid"
                                : ""
                            }`}
                            rows={5}
                            maxLength={BODY_MAX}
                            placeholder="What did you like or dislike? How did you use the product? What should others know before buying?"
                            value={reviewBody}
                            onChange={(e) => {
                              setReviewBody(e.target.value);
                              setFieldErrors((prev) => ({
                                ...prev,
                                reviewBody: undefined,
                                review: undefined,
                              }));
                              setFormError("");
                            }}
                          />
                          <span className="product-review-char-count">
                            {reviewBody.length}/{BODY_MAX}
                          </span>
                        </div>
                        {(fieldErrors.reviewBody || fieldErrors.review) && (
                          <div className="invalid-feedback d-block">
                            {Array.isArray(
                              fieldErrors.reviewBody || fieldErrors.review
                            )
                              ? (fieldErrors.reviewBody || fieldErrors.review)[0]
                              : fieldErrors.reviewBody || fieldErrors.review}
                          </div>
                        )}
                      </div>
                    </div>

                    {formError ? (
                      <p className="text-danger small mt-3 mb-0">{formError}</p>
                    ) : null}
                  </>
                )}
              </div>

              <div className="product-review-submit-bar mt-4">
                <button
                  type="submit"
                  className="btn product-review-submit-btn w-100"
                  disabled={!isFormValid || isSubmitting || reviewLoading}
                >
                  {isSubmitting
                    ? "Submitting…"
                    : myReview
                      ? "Update review"
                      : "Submit review"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </LayoutOne>
    </Fragment>
  );
};

ProductReview.propTypes = {
  userInfo: PropTypes.object,
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
});

export default connect(mapStateToProps)(ProductReview);
